import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { DashboardProvider, useDashboard } from './DashboardContext';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import PeriodPicker from '../widgets/PeriodPicker';
import TitleCardWidget from '../widgets/TitleCardWidget';
import TableCard from '../widgets/TableCard';
import StatCard from '../widgets/StatCard';
import Traffic from '../widgets/Traffic';

import { formatDateTimeISO } from '../../utils/date';
import { useServiceCount, useSystemDashboardUserCount as useUserCount, useServiceHistory, useServiceChartData, useServicePrice } from '../../hooks';
import styles from './Dashboard.module.css';

const SERVICE_USAGE_MAX_ROWS = 4;
const SERVICE_USAGE_COLUMNS = [
    { key: 'name', header: '서비스 이름', width: '1fr', align: 'center' },
    { key: 'count', header: '사용횟수', width: '0.2fr', mobileWidth: '1fr', align: 'center' },
];
const SERVICE_HISTORY_MAX_ROWS = 5;
const SERVICE_HISTORY_COLUMNS = [
    { key: 'date', header: '날짜', width: '200px', align: 'center' },
    { key: 'id', header: '시뮬레이션ID', width: '320px', align: 'center' },
    { key: 'info', header: '시뮬레이션 정보', width: '4fr', minWidth: '260px', align: 'center' },
    { key: 'replay', header: '다시보기', width: '120px', align: 'center', type: 'button' },
];
const SERVICE_HISTORY_COLUMNS_FOR_ADMIN = [
    { key: 'date', header: '날짜', width: '180px', align: 'center', search: false },
    { key: 'name', header: '사용자 이름', width: '180px', align: 'center', search: true },
    { key: 'id', header: '시뮬레이션ID', width: '320px', align: 'center', search: true },
    { key: 'info', header: '시뮬레이션 정보', width: '1fr', search: true },
];

function formatBytes(bytes) {
    if (bytes === null || isNaN(bytes)) return { value: 0, unit: 'Byte' };
    if (bytes === 0) return { value: 0, unit: 'Byte' };

    const units = ['Byte', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);

    return { value: +value.toFixed(2), unit: units[i] };
}

function DashboardPersonalInner() {
    const location = useLocation();
    const queryClient = useQueryClient();

    useEffect(() => {
        // 대시보드로 "돌아올 때" 캐시 무효화 → 자동 재요청 유도
        queryClient.invalidateQueries({ queryKey: ['serviceCount'] });
        queryClient.invalidateQueries({ queryKey: ['servicePrice'] });
        queryClient.invalidateQueries({ queryKey: ['serviceHistory'] });
        queryClient.invalidateQueries({ queryKey: ['serviceChartData'] });
        queryClient.invalidateQueries({ queryKey: ['userCount'] });
    }, [location.key, queryClient]);

    const { user } = useAuth();
    const role = (user?.role || 'USER').toUpperCase();
    const nav = useNavigate();

    const isAdmin = role === 'ADMIN';

    const { year, month } = useDashboard();

    // 서비스별 사용 횟수
    const [serviceCountName, setServiceCountName] = useState('');
    const [searchCountName, setSearchCountName] = useState('');
    useEffect(() => {
        setServiceCountPage(1);
    }, [searchCountName, year, month]);

    const [serviceCountPage, setServiceCountPage] = useState(1);
    const { data: serviceCountRes } = useServiceCount(user?.tenant_id, isAdmin ? 0 : user?.user_key, searchCountName || '', year, month, serviceCountPage, SERVICE_USAGE_MAX_ROWS);

    const serviceCount = useMemo(() => {
        const rows = Array.isArray(serviceCountRes) ? serviceCountRes : [];
        return rows.map((it) => ({
            name: it.name ?? ``,
            count: Number(it.count ?? 0),
        }));
    }, [serviceCountRes]);
    useEffect(() => {
        if (serviceCountName?.length === 0) {
            setSearchCountName(serviceCountName);
        }
    }, [serviceCountName]);

    const [mobileSearchCountOpen, setMobileSearchCountOpen] = useState(false);
    const handleSearchCountName = () => setSearchCountName(serviceCountName);
    const { data: users } = useUserCount(user.tenant_id);

    // 청구요금 계산 필요
    const { data: servicePriceRes } = useServicePrice(user?.tenant_id, isAdmin ? 0 : user?.user_key, year, month);
    const servicePrice = useMemo(() => {
        if (!servicePriceRes) return { price: 0, traffic: 0, storage: 0 };

        return {
            price: servicePriceRes.price,
            traffic: formatBytes(servicePriceRes.total_traffic),
            storage: formatBytes(servicePriceRes.total_storage),
        };
    }, [servicePriceRes]);

    // 서비스 이용 내역
    const [serviceHistoryPage, setHistoryPage] = useState(1);
    const { data: serviceHistoryRes } = useServiceHistory(user?.tenant_id, isAdmin ? 0 : user?.user_key, '', year, month, serviceHistoryPage, SERVICE_HISTORY_MAX_ROWS);
    console.log({ serviceHistoryRes });
    const serviceHistory = useMemo(() => {
        const rows = Array.isArray(serviceHistoryRes?.items) ? serviceHistoryRes.items : [];
        if (isAdmin) {
            return rows.map((it) => ({
                date: formatDateTimeISO(it.working_date, { dateStyle: 'medium', timeStyle: 'short' }) ?? ``,
                id: it.working_id ?? ``,
                name: it.name ?? ``,
                info: it.content ?? ``,
            }));
        }
        return rows.map((it) => {
            const meta = JSON.parse(it.content);
            const replayPayload = {
                id: it.working_id,
                model_id: meta.model_id,
                floor: meta.z,
                x: meta.x,
                y: meta.y,
                metric: meta.metric ?? 'co',
                hrr_density: meta.hrr_density ?? 250,
                thresholds: {
                    co: meta?.thresholds?.co ?? 1500,
                    temp: meta?.thresholds?.temp ?? 60,
                    vis: meta?.thresholds?.vis ?? 5,
                },
            };

            return {
                date: formatDateTimeISO(it.working_date, { dateStyle: 'medium', timeStyle: 'medium' }) ?? ``,
                id: it.working_id ?? ``,
                info: it.content ?? ``,
                replay: true,
                replayPayload,
            };
        });
    }, [serviceHistoryRes]);

    // 다시보기 이벤트
    const handleReplay = (rowOrId) => {
        const row = typeof rowOrId === 'object' ? rowOrId : serviceHistory.find((r) => r.id === rowOrId);
        if (!row?.replay) {
            alert('다시보기는 최근 30일 이내 기록만 가능합니다.');
            return;
        }

        const payload = row.replayPayload ?? {};
        if (!payload?.model_id) {
            alert('다시보기에 필요한 정보가 부족합니다.');
            return;
        }

        // 시뮬 페이지로 이동 + state에 페이로드 전달
        nav(`/simulation?modelId=${encodeURIComponent(payload.model_id)}`, {
            state: { replay: payload },
        });
    };

    // 서비스 트래픽/스토리지 사용량
    const { data: serviceChartDataRes } = useServiceChartData(user?.tenant_id, isAdmin ? 0 : user?.user_key, year, month);
    const { trafficPoints, storagePoints, trafficFmt, storageFmt } = useMemo(() => {
        const src = Array.isArray(serviceChartDataRes?.items) ? serviceChartDataRes.items : [];

        const tBucket = {},
            sBucket = {}; // { day: sumStorage }
        let tSum = 0,
            sSum = 0;

        for (const it of src) {
            const d = new Date(it.working_date);
            const day = d.getDate(); // 로컬 기준 일(day of month)

            const t = Number(it.working_traffic) || 0;
            const s = Number(it.working_storage) || 0;

            tSum += t;
            sSum += s;

            tBucket[day] = (tBucket[day] || 0) + t;
            sBucket[day] = (sBucket[day] || 0) + s;
        }

        const tp = Object.keys(tBucket)
            .map((k) => [Number(k), tBucket[k]])
            .sort((a, b) => a[0] - b[0]);
        const sp = Object.keys(sBucket)
            .map((k) => [Number(k), sBucket[k]])
            .sort((a, b) => a[0] - b[0]);

        const trafficFmt = formatBytes(tSum);
        const storageFmt = formatBytes(sSum);
        return { trafficPoints: tp, storagePoints: sp, trafficFmt, storageFmt };
    }, [serviceChartDataRes]);

    return (
        <div className={styles.wrap}>
            {/* ── 서비스 이용현황 ── */}
            <section className={styles.subsection}>
                <div className={styles.dashGrid21}>
                    <h2 className={styles.subsectionTitle}>서비스 이용현황</h2>
                    <div className={styles.periodPickerWrap}>
                        <PeriodPicker />
                    </div>
                </div>

                <section className={styles.serviceSection}>
                    <div className={role === 'PERSONAL' ? styles.dashGrid21 : styles.dashGrid1}>
                        {isAdmin ? (
                            <div className={styles.dashGrid112}>
                                <StatCard title="청구요금" value={servicePrice.price} unit="₩" className={styles.cardBill} />
                                <StatCard title="사용자수" value={users.count} className={styles.cardBill} />
                                <TitleCardWidget
                                    title="서비스별 사용 횟수"
                                    titleClassName="cardBgLightBlue cardTitleWhite"
                                    headerRight={
                                        <div className={styles.searchBox}>
                                            <input
                                                type="search"
                                                placeholder="서비스 검색"
                                                value={serviceCountName}
                                                onChange={(e) => setServiceCountName(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSearchCountName()}
                                                aria-label="검색어 입력"
                                            />
                                            <button className={styles.iconBtn} onClick={handleSearchCountName} aria-label="검색">
                                                🔍
                                            </button>
                                        </div>
                                    }
                                >
                                    <TableCard
                                        data={serviceCount}
                                        columns={SERVICE_USAGE_COLUMNS}
                                        rows={SERVICE_USAGE_MAX_ROWS}
                                        pagenateSize={SERVICE_USAGE_MAX_ROWS}
                                        page={serviceCountPage}
                                        total={serviceCount.total}
                                        className={styles.cardService}
                                        onPageChange={setServiceCountPage}
                                    />
                                </TitleCardWidget>
                            </div>
                        ) : (
                            <>
                                <TitleCardWidget
                                    title="서비스별 사용 횟수"
                                    titleClassName="cardBgLightBlue cardTitleWhite"
                                    headerRight={
                                        <div className={styles.searchBox}>
                                            <input
                                                type="search"
                                                placeholder="서비스 검색"
                                                value={serviceCountName}
                                                onChange={(e) => setServiceCountName(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSearchCountName()}
                                                aria-label="서비스 검색"
                                            />
                                            <button
                                                className={styles.iconBtn}
                                                onClick={() => {
                                                    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 860;
                                                    if (isMobile) {
                                                        setMobileSearchCountOpen((o) => !o);
                                                    } else {
                                                        handleSearchCountName();
                                                    }
                                                }}
                                                aria-label="검색"
                                            >
                                                🔍
                                            </button>
                                        </div>
                                    }
                                >
                                    <TableCard
                                        data={serviceCount}
                                        columns={SERVICE_USAGE_COLUMNS}
                                        rows={SERVICE_USAGE_MAX_ROWS}
                                        pagenateSize={SERVICE_USAGE_MAX_ROWS}
                                        page={serviceCountPage}
                                        total={serviceCountRes?.total}
                                        className={styles.cardService}
                                        onPageChange={setServiceCountPage}
                                    />
                                </TitleCardWidget>

                                {/* 📱 모바일용 검색 오버레이 (타이틀 위에 겹치는 입력창) */}
                                {mobileSearchCountOpen && (
                                    <div className={styles.searchOverlay}>
                                        <input
                                            type="search"
                                            placeholder="서비스 검색"
                                            value={serviceCountName}
                                            onChange={(e) => setServiceCountName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSearchCountName();
                                                    setMobileSearchCountOpen(false);
                                                }
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            className={styles.iconBtn_blu}
                                            onClick={() => {
                                                handleSearchCountName();
                                                setMobileSearchCountOpen(false);
                                            }}
                                        >
                                            검색
                                        </button>
                                    </div>
                                )}

                                {role === 'PERSONAL' ? <StatCard title="청구요금" value={servicePrice.price} unit="₩" className={styles.cardBill} /> : ''}
                            </>
                        )}
                    </div>
                </section>

                {/* 2행: 1:1 (네트워크 / 스토리지) */}
                <div className={styles.dashGrid11}>
                    <Traffic title="트래픽 사용량" data={trafficPoints} value={trafficFmt.value} unit={trafficFmt.unit} color="#8aa4ff" shape="circle" />
                    <Traffic title="스토리지 사용량" data={storagePoints} value={storageFmt.value} unit={storageFmt.unit} color="#9ba4f8ff" shape="square" />
                </div>
            </section>

            {/* ── 서비스 이용내역 ── */}
            <section className={styles.subsection}>
                <TitleCardWidget title="서비스 이용내역" titleClassName="cardBgLightBlue cardTitleWhite cardTitleHeight">
                    <TableCard
                        data={serviceHistory}
                        columns={isAdmin ? SERVICE_HISTORY_COLUMNS_FOR_ADMIN : SERVICE_HISTORY_COLUMNS}
                        rows={SERVICE_HISTORY_MAX_ROWS}
                        pagenateSize={SERVICE_HISTORY_MAX_ROWS}
                        className={styles.cardService}
                        page={serviceHistoryPage}
                        total={serviceHistoryRes?.total}
                        onReplay={handleReplay}
                        onPageChange={setHistoryPage}
                    />
                </TitleCardWidget>
            </section>
        </div>
    );
}

export default function DashboardPersonal() {
    return (
        <DashboardProvider>
            <DashboardPersonalInner />
        </DashboardProvider>
    );
}
